import { ExclamationCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, message, Modal, Space, Tag, Tooltip } from 'antd';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TRY_THEN_BUY } from '../../../../constants/define/translation';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { API_URLS } from '../../../../constants/urls/api-urls';
import { UserContext } from '../../../../repository/context/userContext';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import {
  postCancelTTBRepository,
  postCloseTTBRepository,
} from '../../../../repository/mytm/try-then-buy/ttbRepository';
import { btnActive } from '../../../../styles';
import { TTBStatus, TTBType } from '../../../../types/tryThenBuy';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { ModalCloseByCS } from '../modals/ModalCloseByCS';
import { ModalReasonInput } from '../modals/ModalReasonInput';

interface ActionsColumnProps {
  record: TTBType;
  onRefresh: () => void;
  isRoleTTBAgent?: boolean;
  getFilteredData?: (data: any) => void;
  isRoleAdmin?: boolean;
  isRoleTTBManager?: boolean;
}

export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(TRY_THEN_BUY);
  const { record, onRefresh, isRoleTTBAgent = false, isRoleAdmin, getFilteredData, isRoleTTBManager } = props;
  const userContext = useContext(UserContext);

  const [isOpenModalCancel, setIsOpenModalCancel] = useState(false);
  const [isOpenModalClose, setIsOpenModalClose] = useState(false);
  const [loading, setLoading] = useState(false);

  const onCloseCancelModal = useCallback(() => {
    setIsOpenModalCancel(false);
  }, []);
  const onOpenCancelModal = useCallback(() => {
    setIsOpenModalCancel(true);
  }, []);
  const onCloseModalClose = useCallback(() => {
    setIsOpenModalClose(false);
  }, []);
  const onOpenModalClose = useCallback(() => {
    setIsOpenModalClose(true);
  }, []);
  const canClose = record.status === TTBStatus.PRODUCT_PICKED_UP || record.status === TTBStatus.PRODUCT_RECEIVED;
  const canCancel =
    record.status !== TTBStatus.CANCELLED &&
    record.status !== TTBStatus.PRODUCT_RETURNED &&
    record.status !== TTBStatus.CHARGED &&
    record.status !== TTBStatus.BOUGHT;

  const handleSubmitCancel = async (value: any) => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.cancel')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        onCloseCancelModal();
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postCancelTTBRepository(API_URLS.CANCEL_TRY_THEN_BUY, {
            orderId: record.orderId,
            cancelReason: value.reason,
          })
            .then(() => {
              onRefresh();
            })
            .catch(e => {
              message.error(MESSAGE_ERROR.M02);
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
        } else {
          setLoading(true);
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              message.error(MESSAGE_ERROR.M02);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      },
    });
  };
  const clearTextSearch = () => {
    getFilteredData && getFilteredData({ strSearch: '' });
  };

  const handleSubmitActionClose = async (value: any) => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.close')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        onCloseModalClose();
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postCloseTTBRepository(API_URLS.CLOSE_TRY_THEN_BUY, {
            orderId: record.orderId,
            amountPaid: value.amount,
            productStatusInfo: value.note,
          })
            .then(() => {
              message.success(t('message.closeTTBSuccess', { orderId: record.orderId }));
              clearTextSearch();
            })
            .catch(e => {
              message.error(MESSAGE_ERROR.M02);
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
        } else {
          setLoading(true);
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              message.error(MESSAGE_ERROR.M02);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      },
    });
  };

  const renderButtonClose = () => {
    return (
      <Tooltip title={t('table.tooltip.close')}>
        <StyledCloseContainer>
          <Button className="close" loading={loading} color="#EEEEEE" onClick={onOpenModalClose}>
            <Tag
              color="#EEEEEE"
              id="test"
              className="close"
              style={{ fontWeight: 600, marginRight: 0, color: 'black' }}
            >
              {t('table.tooltip.close').toUpperCase()}
            </Tag>
          </Button>
        </StyledCloseContainer>
        <ModalCloseByCS
          onSubmit={handleSubmitActionClose}
          isOpenModal={isOpenModalClose}
          onClose={onCloseModalClose}
          title={t('form.closeTTB')}
          label={t('form.amount')}
          placeholder={t('form.placeholderEnter')}
          validateMessage=""
        />
      </Tooltip>
    );
  };

  return (
    <>
      {isRoleTTBAgent ? (
        <Space size="small">{canClose && renderButtonClose()}</Space>
      ) : (
        <StyledContainer>
          <Space size="small">
            {canCancel && (
              <Tooltip title={t('table.tooltip.cancel')}>
                <Button
                  className="cancel"
                  loading={loading}
                  icon={<CloseCircleOutlined />}
                  onClick={onOpenCancelModal}
                />
                <ModalReasonInput
                  onSubmit={handleSubmitCancel}
                  isOpenModal={isOpenModalCancel}
                  onClose={onCloseCancelModal}
                  title={t('form.cancelTTB')}
                  label={t('form.cancelReason')}
                  placeholder={t('form.placeholderEnter')}
                  validateMessage=""
                />
              </Tooltip>
            )}
            {(isRoleAdmin || isRoleTTBManager) && canClose && renderButtonClose()}
          </Space>
        </StyledContainer>
      )}
    </>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-right: 0.5rem;
  ${btnActive}
`;
const StyledCloseContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${btnActive}
`;
